import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft, Shield, ShieldAlert, ShieldCheck, AlertTriangle, MapPin,
  Loader2, Clock, FileText, History, Building2, Calendar, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';

const TIER_STYLES = {
  excellent: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  good: 'bg-cyan-100 text-cyan-800 border-cyan-200',
  review: 'bg-amber-100 text-amber-800 border-amber-200',
  concern: 'bg-red-100 text-red-800 border-red-200',
  'n/a': 'bg-slate-100 text-slate-600 border-slate-200',
};

const fmtDt = (s) => (s ? new Date(s).toLocaleString() : '—');
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString() : '—');
const fmtSecs = (n) => {
  if (!n) return '0s';
  const m = Math.floor(n / 60);
  return m > 0 ? `${m}m` : `${n}s`;
};

/**
 * AdminTalentReliability — Iter-266 (GPS Phase B)
 * Per-talent reliability profile: score breakdown + violation timeline.
 */
const AdminTalentReliability = () => {
  const { talentId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [history, setHistory] = useState({ history: [], count: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!talentId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [p, h] = await Promise.all([
          api.get(`/gps/reliability/talent/${talentId}`),
          api.get(`/gps/reliability/history/${talentId}?limit=100`),
        ]);
        if (cancelled) return;
        setProfile(p.data);
        setHistory(h.data);
      } catch (e) {
        toast.error('Failed to load talent profile');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [talentId]);

  if (loading || !profile) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  const tierClass = TIER_STYLES[profile.tier] || TIER_STYLES['n/a'];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-talent-reliability">
      <header className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Shield className="w-5 h-5 text-cyan-600" />
              Reliability Profile · {profile.talent_name}
            </h1>
            <p className="text-xs text-slate-500">{profile.email}</p>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* SCORE CARD */}
        <Card className="lg:col-span-1">
          <CardContent className="p-6 text-center">
            {profile.talent_photo ? (
              <img src={profile.talent_photo} alt={profile.talent_name} className="w-20 h-20 rounded-full object-cover mx-auto mb-3" />
            ) : (
              <div className="w-20 h-20 rounded-full bg-slate-200 flex items-center justify-center text-slate-500 text-2xl font-bold mx-auto mb-3">
                {profile.talent_name?.[0] || '?'}
              </div>
            )}
            <div className="text-5xl font-bold text-slate-900" data-testid="reliability-score">
              {profile.reliability_score ?? '—'}
            </div>
            <div className="text-xs text-slate-500 mb-2">Reliability Score (0–100)</div>
            <Badge className={tierClass}>
              {profile.tier === 'excellent' && <ShieldCheck className="w-3 h-3 mr-1" />}
              {profile.tier === 'good' && <Shield className="w-3 h-3 mr-1" />}
              {profile.tier === 'review' && <ShieldAlert className="w-3 h-3 mr-1" />}
              {profile.tier === 'concern' && <AlertTriangle className="w-3 h-3 mr-1" />}
              {profile.tier}
            </Badge>
            <div className="text-xs text-slate-500 mt-3">
              {profile.total_shifts} completed shift{profile.total_shifts === 1 ? '' : 's'}
            </div>
          </CardContent>
        </Card>

        {/* SCORE BREAKDOWN */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Score Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <BreakdownRow label="GPS disabled mid-shift" count={profile.violations.gps_disabled} penalty={profile.deductions.gps_disabled} severity="red" />
              <BreakdownRow label="Geofence exit > 15 min" count={profile.violations.geofence_exit} penalty={profile.deductions.geofence_exit} severity="amber" />
              <BreakdownRow label="Attendance rejected by admin" count={profile.violations.attendance_rejected} penalty={profile.deductions.attendance_rejected} severity="red" />
              <BreakdownRow label="Left shift > 30 min early" count={profile.violations.early_leave} penalty={profile.deductions.early_leave} severity="amber" />
              <BreakdownRow label="No clock-in (booked + no-show)" count={profile.violations.no_clock_in} penalty={profile.deductions.no_clock_in} severity="red" />
              <BreakdownRow label="Recap not submitted" count={profile.violations.recap_missing} penalty={profile.deductions.recap_missing} severity="slate" />
            </div>
            <div className="mt-4 pt-3 border-t flex items-center justify-between text-sm">
              <div className="text-slate-500">
                Total points deducted:
                <span className="text-slate-900 font-semibold ml-2">
                  −{Object.values(profile.deductions).reduce((a, b) => a + b, 0)}
                </span>
              </div>
              <div className="text-xs text-slate-400">
                Last activity {profile.last_activity_at ? fmtDate(profile.last_activity_at) : '—'}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* HISTORY TIMELINE */}
        <Card className="lg:col-span-3">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <History className="w-4 h-4 text-cyan-600" />
              Violation History ({history.count})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {history.history.length === 0 ? (
              <div className="text-center py-12 text-slate-500" data-testid="no-violations-state">
                <ShieldCheck className="w-12 h-12 mx-auto mb-3 text-emerald-500" />
                No violations on record. Clean history.
              </div>
            ) : (
              <div className="divide-y">
                {history.history.map((row) => (
                  <div key={row.booking_id} className="px-4 py-3 hover:bg-slate-50" data-testid={`history-row-${row.booking_id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 text-sm font-medium text-slate-900">
                        <Building2 className="w-4 h-4 text-slate-400" />
                        {row.company_name || 'Unknown Client'}
                        <span className="text-slate-400">·</span>
                        <Calendar className="w-3 h-3 text-slate-400" />
                        {fmtDate(row.shift_date)}
                        <span className="text-slate-400">·</span>
                        <MapPin className="w-3 h-3 text-slate-400" />
                        {row.venue_name}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/admin/gps-trail/${row.booking_id}`)}
                        data-testid={`view-trail-${row.booking_id}-btn`}
                      >
                        View Trail <ExternalLink className="w-3 h-3 ml-1" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mb-1">
                      {row.violations.gps_disabled && (
                        <Badge className="bg-red-100 text-red-800 border-red-200 text-xs">
                          <ShieldAlert className="w-3 h-3 mr-1" />
                          GPS disabled · {row.violations.gps_disabled_reason}
                        </Badge>
                      )}
                      {row.violations.geofence_exit && (
                        <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-xs">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          Outside footprint · {fmtSecs(row.violations.time_outside_seconds)}
                        </Badge>
                      )}
                      {row.violations.attendance_review_status === 'rejected' && (
                        <Badge className="bg-red-100 text-red-800 border-red-200 text-xs">Attendance Rejected</Badge>
                      )}
                      {row.violations.attendance_review_status === 'needs_review' && (
                        <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-xs">Pending Review</Badge>
                      )}
                      {row.violations.recap_missing && (
                        <Badge className="bg-slate-100 text-slate-700 border-slate-200 text-xs">
                          <FileText className="w-3 h-3 mr-1" /> Recap Missing
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 flex flex-wrap gap-3">
                      <span><Clock className="w-3 h-3 inline mr-1" /> In: {fmtDt(row.clock_in_time)}</span>
                      <span><Clock className="w-3 h-3 inline mr-1" /> Out: {fmtDt(row.clock_out_time)}</span>
                      {row.role_name && <span>{row.role_name}</span>}
                    </div>
                    {row.violations.attendance_review_notes && (
                      <div className="text-xs text-slate-600 mt-1 italic">
                        Note: {row.violations.attendance_review_notes}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

      </div>
    </div>
  );
};

const BreakdownRow = ({ label, count, penalty, severity }) => {
  const tone = {
    red: 'text-red-700',
    amber: 'text-amber-700',
    slate: 'text-slate-600',
  }[severity];
  return (
    <div className="flex items-center justify-between p-2 rounded bg-slate-50/60">
      <span className="text-slate-700">{label}</span>
      <span className="flex items-center gap-3">
        <span className="text-slate-500 text-xs">× {count}</span>
        <span className={`font-semibold ${tone}`}>{penalty > 0 ? `−${penalty}` : '0'}</span>
      </span>
    </div>
  );
};

export default AdminTalentReliability;
